/**
 * アップロード進捗の項目を扱う純粋関数（DOM / API 非依存）。
 *
 * 状態は upload-manager がアプリ全体で 1 つだけ持つ。どの画面から、どのプロジェクトへ
 * 上げても同じキュー・同じ進捗パネルで扱うため、項目は保存先のプロジェクトを持つ。
 */

type UploadStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled'

/** アップロード先。進捗パネルでプロジェクトごとにまとめて表示するため名前も持つ */
export type UploadTarget = {
  projectId: string
  projectName: string
}

export type UploadItem = UploadTarget & {
  id: string
  file: File
  loadedBytes: number
  totalBytes: number
  status: UploadStatus
  error?: string
}

export type UploadSummary = {
  uploading: number
  queued: number
  errored: number
  /** 画面を離れると失われる件数（待機中 + 実行中） */
  pending: number
  hasFinished: boolean
}

export type UploadGroup = UploadTarget & {
  items: UploadItem[]
}

export function createUploadItems(
  target: UploadTarget,
  files: readonly File[],
  makeId: () => string,
): UploadItem[] {
  return files.map((file) => ({
    ...target,
    id: makeId(),
    file,
    loadedBytes: 0,
    totalBytes: file.size,
    status: 'queued',
  }))
}

export function patchUploadItem(
  items: readonly UploadItem[],
  id: string,
  patch: Partial<Omit<UploadItem, 'id'>>,
): UploadItem[] {
  return items.map((it) => (it.id === id ? { ...it, ...patch } : it))
}

export function removeUploadItem(items: readonly UploadItem[], id: string): UploadItem[] {
  return items.filter((it) => it.id !== id)
}

/** 結果が確定し、もう何もしない状態 */
function isFinishedUpload(status: UploadStatus): boolean {
  return status === 'done' || status === 'cancelled' || status === 'error'
}

export function clearFinishedUploads(items: readonly UploadItem[]): UploadItem[] {
  return items.filter((it) => !isFinishedUpload(it.status))
}

export function summarizeUploads(items: readonly UploadItem[]): UploadSummary {
  const count = (status: UploadStatus) => items.filter((it) => it.status === status).length
  const uploading = count('uploading')
  const queued = count('queued')
  return {
    uploading,
    queued,
    errored: count('error'),
    pending: uploading + queued,
    hasFinished: items.some((it) => isFinishedUpload(it.status)),
  }
}

/** 保存先ごとにまとめる。最初に現れた順を保ち、上げた順に見出しが並ぶようにする */
export function groupUploadsByProject(items: readonly UploadItem[]): UploadGroup[] {
  const groups = new Map<string, UploadGroup>()
  for (const it of items) {
    const group = groups.get(it.projectId)
    if (group) {
      group.items.push(it)
    } else {
      groups.set(it.projectId, {
        projectId: it.projectId,
        projectName: it.projectName,
        items: [it],
      })
    }
  }
  return [...groups.values()]
}

export function uploadPercent(item: Pick<UploadItem, 'loadedBytes' | 'totalBytes'>): number {
  if (item.totalBytes === 0) return 0
  return Math.round((item.loadedBytes / item.totalBytes) * 100)
}
